import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { href } from "../lib/route";
import { Avatar } from "./Avatar";

/* THE COACH'S CARD at the foot of the sidebar (W81, the pro web step 1) — initials (or a photo, when the account has one),
 * the name, "Coach"; it OPENS THE ACCOUNT (its five tabs: Your page · Your public name · Requests · Your data · What
 * changed).
 *
 * ⚠️ IT REPLACES `AccountRow`, AND THE DISCLOSURE WENT WITH THE RAIL. `AccountRow` was a `<details>` because a collapsing
 * rail had no room for the account's links and had to close them when it closed. An always-labelled sidebar has one job
 * for this slot — take the coach to their account — so it is one REAL `<a href>`: the back button, middle-click and
 * `aria-current` mean what they mean everywhere else, and there is no open state to get wrong. Sign out lives on the
 * account page (`CoachPage`), never among the sections (his rule, unchanged): a section is a place you go,
 * signing out is something you do to the account.
 *
 * ⚠️ ONE NODE AT EVERY WIDTH. At 375 the bottom bar clips the name and "Coach" (never removes them — they are the link's
 * accessible name) and the disc is the fourth icon, exactly the destination `account-phone-link` used to be.
 */
export function CoachCard({ session, current }: { session: Session; current: "page" | "true" | undefined }) {
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, nickname")
        .eq("id", session.user.id)
        .maybeSingle();
      if (cancelled || data === null || data === undefined) return;
      const joined = [data.first_name, data.last_name].filter(Boolean).join(" ").trim();
      const chosen = joined !== "" ? joined : (data.nickname ?? "").trim();
      if (chosen !== "") setName(chosen);
    })();
    return () => { cancelled = true; };
  }, [session.user.id]);

  // The email is what we always have; the profile name is better when it exists (`profiles` has no email column).
  const shown = name ?? session.user.email ?? "";

  return (
    <a className="shell-card" href={href({ name: "page" })} aria-current={current}>
      <Avatar name={shown} className="account-disc" />
      <span className="shell-card-text">
        <span className="account-name">{shown}</span>
        <span className="shell-card-role">Coach</span>
      </span>
    </a>
  );
}
